import type { MixpanelReplayIdentity } from './MixpanelReplayController';

export interface MixpanelAnalyticsContext {
  readonly identity: MixpanelReplayIdentity | null;
  readonly providerId: number | null;
}

interface WaitingMessage {
  readonly sequence: number;
  readonly expiresAt: number;
  context: MixpanelAnalyticsContext | null;
  readonly action: () => void;
  readonly resolve: () => void;
  readonly reject: (error: unknown) => void;
}

function sameContext(left: MixpanelAnalyticsContext, right: MixpanelAnalyticsContext): boolean {
  return (
    left.identity?.distinctId === right.identity?.distinctId &&
    left.identity?.sessionId === right.identity?.sessionId &&
    left.providerId === right.providerId
  );
}

export class MixpanelAnalyticsBuffer {
  private messages: WaitingMessage[] = [];
  private sequence = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private reportedDiscard = false;

  constructor(private readonly onDiscard: () => void) {}

  public get lastSequence(): number {
    return this.sequence;
  }

  public enqueue(context: MixpanelAnalyticsContext | null, action: () => void): Promise<void> {
    const sequence = ++this.sequence;
    if (this.messages.length >= 100) {
      this.reportDiscard();
      return Promise.resolve();
    }
    const completion = new Promise<void>((resolve, reject) => {
      this.messages.push({
        sequence,
        expiresAt: Date.now() + 30000,
        context,
        action,
        resolve,
        reject,
      });
    });
    this.scheduleExpiry();
    return completion;
  }

  public bindUnknown(context: MixpanelAnalyticsContext): void {
    for (const message of this.messages) {
      if (message.context === null) message.context = context;
    }
  }

  public drain(context: MixpanelAnalyticsContext, throughSequence: number): void {
    const ready = this.takeThrough(throughSequence);
    for (const message of ready) {
      if (
        message.expiresAt <= Date.now() ||
        message.context === null ||
        !sameContext(message.context, context)
      ) {
        this.discardMessage(message);
        continue;
      }
      try {
        message.action();
        message.resolve();
      } catch (error: unknown) {
        message.reject(error);
      }
    }
    this.scheduleExpiry();
  }

  public discardThrough(throughSequence: number): void {
    for (const message of this.takeThrough(throughSequence)) this.discardMessage(message);
    this.scheduleExpiry();
  }

  private takeThrough(sequence: number): WaitingMessage[] {
    const ready = this.messages.filter((message) => message.sequence <= sequence);
    this.messages = this.messages.filter((message) => message.sequence > sequence);
    return ready;
  }

  private discardMessage(message: WaitingMessage): void {
    this.reportDiscard();
    message.resolve();
  }

  private reportDiscard(): void {
    if (!this.reportedDiscard) {
      this.reportedDiscard = true;
      this.onDiscard();
    }
  }

  private scheduleExpiry(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    const first = this.messages[0];
    if (first === undefined) {
      this.reportedDiscard = false;
      return;
    }
    this.timer = setTimeout(() => {
      const now = Date.now();
      const expired = this.messages.filter((message) => message.expiresAt <= now);
      this.messages = this.messages.filter((message) => message.expiresAt > now);
      for (const message of expired) this.discardMessage(message);
      this.scheduleExpiry();
    }, Math.max(0, first.expiresAt - Date.now()));
  }
}
